/**
 * The fleet's scope and state filter, as pure functions.
 *
 * The data planes list has two controls (D4 to D6 of the data planes round 2 plan):
 *
 * 1. The scope: `in-service`, `out-of-service` or `all`. It drives the request. The server takes the
 *    two class names as `?status=`, and no `status` means every state.
 * 2. The state chips: the states present in the loaded rows, each with its count. A pressed chip
 *    narrows the loaded rows in the browser. No chip is pressed by default.
 *
 * The server loads a scope in full, with no `limit`, so a count per state over the loaded rows is
 * exact for every scope.
 *
 * The state vocabulary is injected. The source of truth is `site-admin-api`'s
 * `modules/dp-admin/dp-lifecycle.ts`, which this package cannot import. `GET .../data-planes/states`
 * serves it at runtime. Do not write a literal state list in this file: a client copy proves that it
 * covers the union and never that it agrees with the server.
 */
import type { DataPlaneSummary } from '../api/dataPlanes.api';

/** The scope of the fleet request. Two are server class names; `all` sends no `status`. */
export type DpScope = 'in-service' | 'out-of-service' | 'all';

/** The three scope tokens, in the order the scope control draws them. The allowlist for a stored or told value. */
export const DP_SCOPES: readonly DpScope[] = ['in-service', 'out-of-service', 'all'];

/** The scope of the default view (D9): the in-service data planes. */
export const DEFAULT_DP_SCOPE: DpScope = 'in-service';

/** `true` when `value` is one of {@link DP_SCOPES}. An exact match: no trim, no case fold. */
export function isDpScope(value: string): value is DpScope {
  return (DP_SCOPES as readonly string[]).includes(value);
}

/**
 * A state's display label: the wire value with its first letter in capitals. The wire value stays
 * as it is and is what reaches `?status=`. An unknown value still gets a label, because a state the
 * console cannot label is one the operator must see.
 */
export function stateLabel(wireValue: string): string {
  if (wireValue === '') return '';
  return wireValue.charAt(0).toUpperCase() + wireValue.slice(1);
}

/** The sticky filter as the pane reads it: one scope and the pressed states. */
export interface ScopeSelection {
  readonly scope: DpScope;
  /** The pressed states, in stored order, without duplicates. Empty: no chip narrows the rows. */
  readonly states: readonly string[];
}

/**
 * Read a stored filter value (D8). The stored array holds one scope token and the pressed states.
 *
 * - Exactly one distinct scope token: that scope, and every other value as a pressed state, in
 *   order, without duplicates.
 * - No scope token, or two different ones: the default view, scope `in-service` and no pressed
 *   state. So a legacy array of nine states restores the default, never nine pressed chips.
 *
 * The caller validates the states against the vocabulary first; the sticky sanitiser does that.
 * Pure; it never throws.
 */
export function parseScopeSelection(values: readonly string[]): ScopeSelection {
  const scopes = new Set(values.filter(isDpScope));
  if (scopes.size !== 1) return { scope: DEFAULT_DP_SCOPE, states: [] };
  const [scope] = scopes;
  return { scope, states: [...new Set(values.filter((value) => !isDpScope(value)))] };
}

/** The stored form of a selection: the scope token, then the pressed states. The inverse of {@link parseScopeSelection}. */
export function scopeSelectionValues(selection: ScopeSelection): readonly string[] {
  return [selection.scope, ...selection.states];
}

/**
 * The `?status=` value for a scope, or `undefined` to send no `status`.
 *
 * `in-service` and `out-of-service` are the server's class names and go as they are. `all` sends
 * nothing, which the server reads as every state.
 */
export function scopeStatusParam(scope: DpScope): string | undefined {
  return scope === 'all' ? undefined : scope;
}

/**
 * The count per state over the loaded rows, for the state chips (D6).
 *
 * Output: a map in vocabulary order that holds only the vocabulary states with one row or more. A
 * row whose status the vocabulary does not hold counts nowhere, so it draws no chip. Pure.
 */
export function stateCounts(
  dps: readonly DataPlaneSummary[],
  vocabulary: readonly string[],
): ReadonlyMap<string, number> {
  const tally = new Map<string, number>();
  for (const dp of dps) tally.set(dp.status, (tally.get(dp.status) ?? 0) + 1);
  const counts = new Map<string, number>();
  for (const state of vocabulary) {
    const count = tally.get(state);
    if (count !== undefined) counts.set(state, count);
  }
  return counts;
}

/** The most entries a stored vocabulary may hold (security-1 of the round 2 review). The server serves 12 today. */
export const STORED_VOCABULARY_MAX = 64;

/** The shape of one stored state: a lower-case letter, then lower-case letters or hyphens, 32 characters at most. */
export const STORED_STATE_PATTERN = /^[a-z][a-z-]{0,31}$/;

/**
 * Read the state vocabulary a previous session stored, the raw string from browser storage.
 *
 * The result becomes the sticky sanitiser's allowlist until `GET .../data-planes/states` answers,
 * and anything that can write the origin's storage can write it, so it is bounded first.
 * Output: the parsed array when `raw` is JSON for an array of at most {@link STORED_VOCABULARY_MAX}
 * strings, each matching {@link STORED_STATE_PATTERN}. Else `[]`: for `null`, unparseable JSON, a
 * non-array, too many entries, or one entry of the wrong type or shape. One bad entry rejects the
 * whole array, so a partial list never reads as the vocabulary. Pure; it never throws.
 */
export function parseStoredVocabulary(raw: string | null): readonly string[] {
  if (raw === null) return [];
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(parsed) || parsed.length > STORED_VOCABULARY_MAX) return [];
  const states: string[] = [];
  for (const value of parsed) {
    if (typeof value !== 'string' || !STORED_STATE_PATTERN.test(value)) return [];
    states.push(value);
  }
  return states;
}

/**
 * What the rows region renders.
 *
 * - `empty-fleet`: the request returned no row for the scope.
 * - `no-matches`: rows loaded, and the filters hide every one.
 * - `rows`: the rows to draw.
 */
export type FleetVisibility =
  | { readonly kind: 'rows'; readonly rows: readonly DataPlaneSummary[] }
  | { readonly kind: 'no-matches' }
  | { readonly kind: 'empty-fleet' };

/** The rows whose status is any of the selected states: a union within the filter. */
export function filterByStates(
  dps: readonly DataPlaneSummary[],
  selected: readonly string[],
): readonly DataPlaneSummary[] {
  const selectedSet = new Set(selected);
  return dps.filter((dp) => selectedSet.has(dp.status));
}

/**
 * Apply the pressed states, and name what the rows region renders.
 *
 * Inputs: `loaded`, every row the scope request returned; `matched`, the rows of `loaded` that pass
 * the text filters (search, region, plan); `pressed`, the pressed states. An empty `pressed`
 * narrows nothing: no chip is pressed by default (D6).
 * Output: `empty-fleet` when `loaded` is empty; else `no-matches` when no row of `matched` is in
 * `pressed`; else `rows`, in the order of `matched`. Pure.
 */
export function fleetVisibility(
  loaded: readonly DataPlaneSummary[],
  matched: readonly DataPlaneSummary[],
  pressed: readonly string[],
): FleetVisibility {
  if (loaded.length === 0) return { kind: 'empty-fleet' };
  const rows = pressed.length === 0 ? matched : filterByStates(matched, pressed);
  return rows.length === 0 ? { kind: 'no-matches' } : { kind: 'rows', rows };
}
